import { categoryKeyForCode, passengerCategoryKeys } from './ssrCategories.js';
import { getPassengerReward, isPriorityServiceCode } from './rewardTier.js';

const WHEELCHAIR_CODES = ['WCHC', 'WCHR', 'WCHS', 'WCBD', 'WCBW'];


const OTHER_INFO_CODES = [
    'AVIH',
    'PETC',
    'WEAP',
    'ALGC',
    'SVAN',
    'DEPA',
    'DEPU',
    'INAD',
    'PICA',
    'PICU',
    'AOXY',
    'POXY',
];

const EXTRA_SEAT_CODES = ['CBBG', 'EXST']; // Extra seat types (will be filtered out from passenger lists)
const INFANT_CODE = 'INFT';
const ID_STAFF_CODE = 'ID STAFF';
const MEAL_SUFFIX = 'ML';

export const normalizeService = (service) => {
    if (typeof service === 'string') {
        return {
            code: service,
            description: null,
            text: null,
        };
    }

    if (typeof service === 'object' && service !== null) {
        return {
            code: service.code || service.Code || '',
            description: service.description || null,
            text: service.text || null,
        };
    }

    return { code: '', description: null, text: null };
};

const normalizeServices = (services) => {
    if (!Array.isArray(services)) return [];
    return services.map(normalizeService).filter(s => s.code);
};

const isMealCode = (code) => {
    if (!code || typeof code !== 'string') return false;
    const normalized = code.toUpperCase();
    return normalized.endsWith(MEAL_SUFFIX) || normalized === 'MEAL';
};

const isWheelchairCode = (code) => {
    if (!code || typeof code !== 'string') return false;
    const normalized = code.toUpperCase();
    return WHEELCHAIR_CODES.includes(normalized) || normalized.startsWith('WC');
};

const isSpecialNeedsCode = (code) => {
    if (!code || typeof code !== 'string') return false;
    return categoryKeyForCode(code) !== null && !isWheelchairCode(code);
};

export const isOtherInfoCode = (code) => {
    if (!code || typeof code !== 'string') return false;
    const normalized = code.toUpperCase();

    // Explicit other info codes
    if (OTHER_INFO_CODES.includes(normalized)) return true;
    if (
        !isWheelchairCode(code) &&
        !isSpecialNeedsCode(code) &&
        !isMealCode(code) &&
        !isPriorityServiceCode(normalized) &&
        normalized !== ID_STAFF_CODE &&
        !EXTRA_SEAT_CODES.includes(normalized) &&
        normalized !== INFANT_CODE
    ) {
        return true;
    }

    return false;
};

export const classifyPassengerServices = (passenger) => {
    const services = normalizeServices(passenger?.services || []);

    const wheelchairCodes = [];
    const specialNeedsCodes = [];
    const otherInfoCodes = [];
    const mealCodes = [];

    let hasPriority = false;
    let hasIdStaff = false;

    const reward = getPassengerReward(passenger);

    // Check services array
    services.forEach(service => {
        const code = service.code;

        if (isWheelchairCode(code)) {
            wheelchairCodes.push(code);
        } else if (isSpecialNeedsCode(code)) {
            specialNeedsCodes.push(code);
        } else if (isMealCode(code)) {
            mealCodes.push(code);
        } else if (isPriorityServiceCode(code)) {
            hasPriority = true;
        } else if (code.toUpperCase() === ID_STAFF_CODE) {
            hasIdStaff = true;
        } else if (isOtherInfoCode(code)) {
            otherInfoCodes.push(code);
        }
    });

    // Also check separate properties (backward compatibility)
    if (passenger.isStaff === true) {
        hasIdStaff = true;
    }


    // Check wheelchair property (backward compatibility)
    if (passenger.wheelchair === true && wheelchairCodes.length === 0) {
        wheelchairCodes.push('WCHR'); // Default wheelchair code
    }

    return {
        hasWheelchair: wheelchairCodes.length > 0,
        hasSpecialNeeds: specialNeedsCodes.length > 0,
        hasPriority,
        hasIdStaff,
        hasOtherInfo: otherInfoCodes.length > 0,
        hasInfant: passenger.isInfant === true,
        reward,

        wheelchairCodes,
        specialNeedsCodes,
        otherInfoCodes,
        mealCodes,

        // All non-meal, non-priority, non-staff service codes
        allServiceCodes: services
            .map(s => s.code)
            .filter(code =>
                !isMealCode(code) &&
                !isPriorityServiceCode(code) &&
                code.toUpperCase() !== ID_STAFF_CODE
            ),
    };
};

export const hasAnySpecialNeeds = (passenger) => passengerCategoryKeys(passenger).length > 0;

/**
 * Special needs keeps the seat icon, except when Show reward passengers is
 * checked and the special needs filter is off. Then the reward mark wins.
 */
export const shouldPrioritizeSpecialNeedsIcon = (selectedFilter, passenger) => {
    if (!hasAnySpecialNeeds(passenger)) return false;
    if (selectedFilter?.showRewardStatus === true && selectedFilter?.SPECIAL_NEEDS !== true) {
        return false;
    }
    return true;
};

/**
 * Reward marks show by default. Special needs replaces them on a shared seat
 * unless only Show reward passengers is checked.
 */
export const planSeatIcons = (selectedFilter, passenger, services) => {
    const classification = classifyPassengerServices(passenger);
    const list = Array.isArray(services) ? services : [];
    const hideReward = shouldPrioritizeSpecialNeedsIcon(selectedFilter, passenger);

    return {
        classification,
        rewardInPrimary: !hideReward,
        rowServices: list,
    };
};

/**
 * Returns true when a passenger matches a reward tier filter key.
 */
export const passengerMatchesRewardTier = (passenger, tierKey) => {
    if (!tierKey) {
        return false;
    }

    const classification = classifyPassengerServices(passenger);
    return classification.reward?.tierKey === tierKey;
};
